import { Alignment, Classes, PanelProps, Switch } from '@blueprintjs/core';
import * as React from 'react';

import { TEST_IDS } from 'src/constants/testIds';
import {
  useDefaultModelsInfo,
  useFindModel,
  useModelNamePlaceholderByModelId,
  useModelNamedGraphPlaceholderByModelId,
  usePublish,
  useValidateModelsForSubmission,
} from 'src/containers/PublishProjectDrawer/hooks';
import {
  MappingConfigPanel,
  MappingConfigPanelProps,
} from 'src/containers/PublishProjectDrawer/MappingConfigPanel';
import {
  ModelAliasConfigPanel,
  ModelAliasConfigPanelProps,
} from 'src/containers/PublishProjectDrawer/ModelAliasConfigPanel';
import { ModelConfigPanelModels } from 'src/containers/PublishProjectDrawer/ModelConfigModels';
import {
  ModelNamespacePanel,
  ModelNamespacePanelProps,
} from 'src/containers/PublishProjectDrawer/ModelNamespacePanel';
import {
  OpenInExplorerPanel,
  OpenInExplorerPanelProps,
} from 'src/containers/PublishProjectDrawer/OpenInExplorerPanel';
import { PublishProjectDrawerFooter } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerFooter';
import { PublishProjectDrawerStepper } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerStepper';
import {
  CreationType,
  DatabaseInfo,
  ModelInfo,
  PrefixInfo,
  SensitivePropertyGroupsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { hasValidMappingsSelector } from 'src/selectors/hasValidMappings';
import { isDatabaseRefreshedSelector } from 'src/selectors/isRefreshingDatabase';
import { ModelSelectItem } from 'src/selectors/modelItems';
import { projectSelector } from 'src/selectors/project';
import {
  projectInvalidRulesByIdSelector,
  projectRulesByIdSelector,
} from 'src/selectors/projectRules';
import * as copy from 'src/templates/copy';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export type ModelConfigPanelProps = {
  databaseInfo: DatabaseInfo;
  spgInfo: SensitivePropertyGroupsInfo;
};

enum ModelConfigPanelNextButtonType {
  COMBO_MODEL = 'COMBO_MODEL',
  MAPPING = 'MAPPING',
  PREFIX = 'PREFIX',
  PUBLISH = 'PUBLISH',
}

export const ModelConfigPanel: React.VFC<PanelProps<ModelConfigPanelProps>> = ({
  closePanel,
  openPanel,
  databaseInfo,
  spgInfo,
}) => {
  const { databaseId } = databaseInfo;
  const { projectId } = useRouterParams();
  const publish = usePublish();

  const databasesById = useReduxSelector(
    (state) => state.databases.databasesById
  );
  const databaseNamespaces = useReduxSelector(
    (state) => state.databases.databasesById[databaseId]?.namespaces || {}
  );
  const hasValidMappings = useReduxSelector(
    hasValidMappingsSelector(projectId)
  );
  const project = useReduxSelector(projectSelector(projectId));
  const ruleInfo = useReduxSelector(projectRulesByIdSelector(projectId));

  const didDatabaseChange = useDidValueChange(databaseId);
  const refreshedDatabase = useReduxSelector(
    isDatabaseRefreshedSelector(databaseId)
  );
  const didRefreshedDatabaseChange = useDidValueChange(refreshedDatabase);

  const findModel = useFindModel(databaseId);
  const validateModels = useValidateModelsForSubmission(
    databaseId,
    project?.targetModelId
  );
  const modelNamePlaceholderByModelId =
    useModelNamePlaceholderByModelId(databaseId);
  const namedGraphIriPlaceholderByModelId =
    useModelNamedGraphPlaceholderByModelId(databaseId);

  const [isPublishingRules, setIsPublishingRules] = React.useState(
    !Object.keys(ruleInfo).length
      ? false
      : project?.publishHistory.isPublishingRules ?? true
  );

  const initialModelsInfo = useDefaultModelsInfo(databaseId);
  const initialExistingByModelId = Object.entries(initialModelsInfo).reduce<
    Record<string, ModelSelectItem | null>
  >((acc, [modelId, modelInfo]) => {
    const modelSelectItem = findModel(modelInfo.modelName);
    acc[modelId] = modelSelectItem?.namedGraphIris.length
      ? modelSelectItem
      : null;

    return acc;
  }, {});
  const [existingByModelId, setExistingByModelId] = React.useState<
    Record<string, ModelSelectItem | null>
  >(initialExistingByModelId);
  const [modelsInfo, setModelsInfo] = React.useState(initialModelsInfo);
  const [isConsolidatingModels, setIsConsolidatingModels] = React.useState(
    Boolean(project?.publishHistory.isConsolidatingModels)
  );

  React.useEffect(() => {
    if (
      (didDatabaseChange || didRefreshedDatabaseChange) &&
      refreshedDatabase
    ) {
      setModelsInfo(initialModelsInfo);
      setExistingByModelId(initialExistingByModelId);
    }
  }, [
    didDatabaseChange,
    didRefreshedDatabaseChange,
    initialExistingByModelId,
    initialModelsInfo,
    project,
    refreshedDatabase,
  ]);

  const handleSetInfoForModel = React.useCallback(
    (modelId: string, nextInfo: Partial<ModelInfo>) => {
      const prevInfo = modelsInfo[modelId];
      setModelsInfo({
        ...modelsInfo,
        [modelId]: {
          ...prevInfo,
          ...nextInfo,
        },
      });
    },
    [modelsInfo]
  );

  const handleSetIsConsolidating = React.useCallback(
    (isConsolidating: boolean) => {
      if (!project) {
        return;
      }
      setIsConsolidatingModels(isConsolidating);

      const initialExisting = initialExistingByModelId[project.targetModelId];
      setExistingByModelId({
        ...existingByModelId,
        [project.targetModelId]: initialExisting
          ? { ...initialExisting }
          : null,
      });
      handleSetInfoForModel(
        project.targetModelId,
        initialModelsInfo[project.targetModelId]
      );
    },
    [
      existingByModelId,
      handleSetInfoForModel,
      initialExistingByModelId,
      initialModelsInfo,
      project,
    ]
  );

  const handleSelectExistingForModel = React.useCallback(
    (modelId: string, existing: ModelSelectItem | null) => {
      setExistingByModelId({
        ...existingByModelId,
        [modelId]: existing,
      });
      if (existing?.value) {
        handleSetInfoForModel(modelId, {
          modelName: existing.value,
          namedGraphIri: existing.namedGraphIris.length
            ? existing.namedGraphIris[0]
            : initialModelsInfo[modelId]?.namedGraphIri || '',
        });
      }
    },
    [existingByModelId, handleSetInfoForModel, initialModelsInfo]
  );

  const handleChangeCreationTypeForModel = React.useCallback(
    (modelId: string, value: CreationType) => {
      const newCreationType = value as CreationType;
      const existing = existingByModelId[modelId];
      const modelNamePlaceholder = modelNamePlaceholderByModelId[modelId];
      const namedGraphIriPlaceholder =
        namedGraphIriPlaceholderByModelId[modelId];

      if (newCreationType === CreationType.NEW) {
        handleSetInfoForModel(modelId, {
          creationType: newCreationType,
          modelName: modelNamePlaceholder,
          namedGraphIri: namedGraphIriPlaceholder,
        });
      } else if (newCreationType === CreationType.REPLACE) {
        handleSetInfoForModel(modelId, {
          creationType: newCreationType,
          modelName: existing?.value || '',
          namedGraphIri: existing?.namedGraphIris.length
            ? existing.namedGraphIris[0]
            : initialModelsInfo[modelId]?.namedGraphIri || '',
        });
      } else if (newCreationType === CreationType.APPEND) {
        handleSetInfoForModel(modelId, {
          creationType: newCreationType,
          modelName: existing?.value || '',
          namedGraphIri: existing?.namedGraphIris.length
            ? existing.namedGraphIris[0]
            : initialModelsInfo[modelId]?.namedGraphIri || '',
        });
      }
    },
    [
      existingByModelId,
      handleSetInfoForModel,
      initialModelsInfo,
      modelNamePlaceholderByModelId,
      namedGraphIriPlaceholderByModelId,
    ]
  );

  const nextButtonError = React.useMemo(() => {
    const modelsError = validateModels(modelsInfo, isConsolidatingModels);
    if (modelsError) {
      return modelsError;
    }

    return '';
  }, [validateModels, modelsInfo, isConsolidatingModels]);

  const conflictedPrefixes = React.useMemo<Set<string>>(() => {
    if (!project) {
      return new Set();
    }

    const publishedModelNamespacesByPrefix = Object.values(modelsInfo).reduce<
      Record<string, string>
    >((acc, modelInfo) => {
      if (modelInfo.shouldPublish) {
        const projectModel = project.modelsById[modelInfo.projectModelId];
        if (projectModel?.prefix) {
          acc[projectModel.prefix] = projectModel.namespace;
        }
      }
      return acc;
    }, {});

    const conflictedPrefixes = new Set<string>();
    Object.entries(databaseNamespaces).forEach(
      ([databasePrefix, databaseNamespace]) => {
        const existingNamespace =
          publishedModelNamespacesByPrefix[databasePrefix];
        if (existingNamespace && existingNamespace !== databaseNamespace) {
          conflictedPrefixes.add(databasePrefix);
        }
      }
    );

    return conflictedPrefixes;
  }, [databaseNamespaces, modelsInfo, project]);

  const [nextButtonType, nextButtonTestId] = React.useMemo<
    [ModelConfigPanelNextButtonType, string]
  >(() => {
    const publishModelCount = Object.values(modelsInfo).reduce(
      (acc, modelInfo) => {
        if (modelInfo.shouldPublish) {
          return acc + 1;
        }

        return acc;
      },
      0
    );
    const { databaseId } = databaseInfo;

    if (publishModelCount && conflictedPrefixes.size) {
      return [
        ModelConfigPanelNextButtonType.PREFIX,
        TEST_IDS.publishProjectDrawer.footer.nextToPrefixButton,
      ];
    }

    if (!databasesById[databaseId] && publishModelCount < 2) {
      if (hasValidMappings) {
        return [
          ModelConfigPanelNextButtonType.MAPPING,
          TEST_IDS.publishProjectDrawer.footer.nextToMappingButton,
        ];
      }

      return [
        ModelConfigPanelNextButtonType.PUBLISH,
        TEST_IDS.publishProjectDrawer.footer.publishButton,
      ];
    }

    return [
      ModelConfigPanelNextButtonType.COMBO_MODEL,
      TEST_IDS.publishProjectDrawer.footer.nextToAliasButton,
    ];
  }, [
    conflictedPrefixes,
    databaseInfo,
    databasesById,
    hasValidMappings,
    modelsInfo,
  ]);

  const handleNext = React.useCallback(async () => {
    if (!project || nextButtonError) {
      return;
    }

    const listedPrefixesByNamespace: Record<string, string> = {};
    const prefixInfoArr = Object.values(modelsInfo).reduce<PrefixInfo[]>(
      (acc, modelInfo) => {
        const projectModel = project.modelsById[modelInfo.projectModelId];
        if (projectModel) {
          const { namespace, prefix, shouldPublishPrefix } = projectModel;
          // we dedupe here because prefixes might not be unique (we allow dupes to be saved in the settings drawer);
          if (listedPrefixesByNamespace[namespace] === prefix) {
            return acc;
          }

          if (shouldPublishPrefix) {
            const isExisting = Boolean(databaseNamespaces[namespace]);
            const isConflicted = conflictedPrefixes.has(prefix);

            if (isConflicted || !isExisting) {
              listedPrefixesByNamespace[namespace] = prefix;
              acc.push({
                isConflicted,
                namespace,
                prefix,
                shouldPublish: isConflicted ? false : shouldPublishPrefix,
              });
            }
          }
        }

        return acc;
      },
      []
    );

    if (nextButtonType === ModelConfigPanelNextButtonType.PREFIX) {
      const props: ModelNamespacePanelProps = {
        databaseInfo,
        isConsolidatingModels,
        initialPrefixInfoArr: prefixInfoArr,
        isPublishingRules,
        modelsInfo,
        ruleInfo,
        spgInfo,
      };
      openPanel({ renderPanel: ModelNamespacePanel, props });
    } else if (nextButtonType === ModelConfigPanelNextButtonType.MAPPING) {
      const props: MappingConfigPanelProps = {
        databaseInfo,
        isConsolidatingModels,
        isPublishingRules,
        modelsInfo,
        prefixInfoArr,
        ruleInfo,
        spgInfo,
      };
      openPanel({ renderPanel: MappingConfigPanel, props });
    } else if (nextButtonType === ModelConfigPanelNextButtonType.PUBLISH) {
      const props: OpenInExplorerPanelProps = {
        databaseInfo,
        isPublishingRules,
        modelsInfo,
        ruleInfo,
      };
      const success = await publish({
        ...props,
        isConsolidatingModels,
        prefixInfoArr,
        projectId,
        spgInfo,
      });
      if (success) {
        openPanel({ renderPanel: OpenInExplorerPanel, props });
      }
    } else {
      const props: ModelAliasConfigPanelProps = {
        databaseInfo,
        isConsolidatingModels,
        isPublishingRules,
        modelsInfo,
        prefixInfoArr,
        ruleInfo,
        spgInfo,
      };
      openPanel({ renderPanel: ModelAliasConfigPanel, props });
    }
  }, [
    conflictedPrefixes,
    databaseNamespaces,
    databaseInfo,
    isConsolidatingModels,
    isPublishingRules,
    modelsInfo,
    nextButtonError,
    nextButtonType,
    openPanel,
    project,
    projectId,
    publish,
    ruleInfo,
    spgInfo,
  ]);

  const invalidRules = useReduxSelector(
    projectInvalidRulesByIdSelector(projectId)
  );

  const ruleWarning = React.useMemo(() => {
    if (isPublishingRules && Object.keys(invalidRules).length > 0) {
      return copy.components.publishProjectDrawer.modelConfiguration.rules
        .incompleteRulesWarning;
    }
    return '';
  }, [isPublishingRules, invalidRules]);

  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.publishProjectDrawer.model.container}
      >
        <PublishProjectDrawerStepper active="model" />
        <ModelConfigPanelModels
          databaseId={databaseId}
          existingByModelId={existingByModelId}
          handleChangeCreationTypeForModel={handleChangeCreationTypeForModel}
          handleSelectExistingForModel={handleSelectExistingForModel}
          handleSetInfoForModel={handleSetInfoForModel}
          handleSetIsConsolidating={handleSetIsConsolidating}
          isConsolidating={isConsolidatingModels}
          modelsInfo={modelsInfo}
        />

        {Object.keys(ruleInfo).length ? (
          <div className="sd-drawer-group-label">
            <Switch
              alignIndicator={Alignment.RIGHT}
              checked={isPublishingRules}
              className="sd-label-switch"
              data-testid={TEST_IDS.publishProjectDrawer.model.rules.toggle}
              inline
              label={
                copy.components.publishProjectDrawer.modelConfiguration.rules
                  .label
              }
              onChange={() => setIsPublishingRules(!isPublishingRules)}
            />
          </div>
        ) : null}
      </div>

      <PublishProjectDrawerFooter
        handleBack={closePanel}
        handleNext={handleNext}
        nextError={nextButtonError}
        nextTestId={nextButtonTestId}
        nextText={
          nextButtonType === ModelConfigPanelNextButtonType.COMBO_MODEL ||
          nextButtonType === ModelConfigPanelNextButtonType.MAPPING ||
          nextButtonType === ModelConfigPanelNextButtonType.PREFIX
            ? copy.components.publishProjectDrawer.footer.next
            : copy.components.publishProjectDrawer.footer.publish
        }
        warningCallout={ruleWarning}
      />
    </>
  );
};
